import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VideoPlayerDialogProps {
  videoId: string | null;
  isOpen: boolean;
  onClose: () => void;
  sessionTitle?: string;
}

// Helper function to extract video ID from YouTube URL or return ID as-is
const extractVideoId = (input: string): string => {
  // If it's already a video ID (11 characters, alphanumeric with dashes/underscores)
  if (/^[\w-]{11}$/.test(input)) {
    return input;
  }
  
  // Try to extract from various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /youtube\.com\/.*[?&]v=([^&]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) {
      return match[1];
    }
  }
  
  // If no pattern matches, return the input as-is
  return input;
};

export function VideoPlayerDialog({
  videoId,
  isOpen,
  onClose,
  sessionTitle,
}: VideoPlayerDialogProps) {
  if (!videoId) return null;

  // Extract clean video ID from input (handles both full URLs and IDs)
  const cleanVideoId = extractVideoId(videoId);

  // YouTube embed URL with security parameters
  const embedUrl = `https://www.youtube-nocookie.com/embed/${cleanVideoId}?autoplay=1&disablekb=1&fs=1&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1&controls=1`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl w-[95vw] h-[95vh] p-0 bg-black border-0 overflow-hidden">
        <DialogTitle className="sr-only">
          {sessionTitle || "વિડિયો પ્લેયર"}
        </DialogTitle>
        
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 z-50 bg-black/50 hover:bg-black/70 text-white rounded-full"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Header with title */}
        {sessionTitle && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 pt-6 z-40">
            <h2 className="text-white text-lg font-semibold text-center">
              {sessionTitle}
            </h2>
          </div>
        )}

        {/* Video iframe */}
        <div className="relative w-full h-full flex items-center justify-center">
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              border: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            title={sessionTitle || "Video Player"}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
